import externalLinkIcon from "@assets/images/external-link.svg";
import trashTeal from "@assets/images/trash-teal.svg";
import { Button, Grid, Paper, Typography } from "@material-ui/core";
import DatePicker from "@saleor/components/DatePicker/DatePicker";
import DecimalFormat from "@saleor/components/DecimalFormat";
import DialogConfirmDelete from "@saleor/components/DialogConfirmDelete";
import MyTypography from "@saleor/components/MyTypography";
import NumberFormat from "@saleor/components/NumberFormat";
import SingleTextField from "@saleor/components/SingleTextField";
import { REGEX_ONLY_NUMBER_CHARACTER } from "@saleor/config";
import { ShippingTypeEnum } from "@saleor/createOrder/components/CollectionForm/CustomerData/Shipping";
import ModalNote from "@saleor/createOrder/components/ProductDetails/ModalNote";
import { ValidateFormProps } from "@saleor/createOrder/views/CreateOrderView";
import { ContractOrderQuery } from "@saleor/graphql";
import { parseDateString } from "@saleor/utils/date";
import { formatThousandInteger } from "@saleor/utils/number";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import * as Yup from "yup";

import ModalShipping from "./ModalShipping";
import useStyles from "./styles";

interface ProductDetailsProps {
  dataOrder: ContractOrderQuery;
  refetch: () => void;
  listLine: any[];
  setListLine: (value) => void;
  validateForm: ValidateFormProps;
  setValidateForm: (values: ValidateFormProps) => void;
}

interface deleteSingleLine {
  id: string;
  title: string;
  content: string;
}

interface modalNoteData {
  name: string;
  title: string;
  subTitle?: string;
  initialNote?: string;
  lineId: string;
}

const ProductDetails: React.FC<ProductDetailsProps> = props => {
  const {
    dataOrder,
    listLine,
    setListLine,
    validateForm,
    setValidateForm
  } = props;
  const classes = useStyles();
  const intl = useIntl();

  const [openConfrmDelete, setOpenConfirmDelete] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [dataDeleteSingle, setDataDeleteSingle] = useState<deleteSingleLine>({
    content: "",
    id: "",
    title: ""
  });
  const [dataModalNote, setDataModalNote] = useState<modalNoteData>({
    title: "",
    subTitle: "",
    name: "",
    initialNote: "",
    lineId: ""
  });
  const [openModalNote, setOpenModalNote] = useState<boolean>(false);
  const [dataModalShipping, setDataModalShipping] = useState({
    address: [],
    lineId: "",
    visible: false,
    initialValue: ""
  });
  const [modalNoteKey, setModalNoteKey] = useState<number>(0);

  const SignupSchema = Yup.object().shape({
    quantity: Yup.array().of(Yup.number().required("Required")),
    plant: Yup.array().of(
      Yup.string()
        .length(4, "Too short")
        .required("Required")
    ),
    requestDate: Yup.array().of(
      Yup.date()
        .transform(parseDateString)
        .min(new Date())
        .required("Required")
    )
  });

  const formik = useFormik({
    initialValues: {
      quantity: listLine.map(line => line.quantity),
      plant: listLine.map(line => line.plant),
      requestDate: listLine.map(line => line.requestDate)
    },
    onSubmit: values => {
      const lines = [...listLine];
      lines.forEach((line, index) => {
        line[index].quantity = values.quantity[index];
        line[index].plant = values.plant[index];
        line[index].requestDate = values.requestDate[index];
      });
      setListLine(lines);
    },
    validationSchema: SignupSchema
  });

  useEffect(() => {
    if (listLine?.length) {
      formik.handleChange({
        target: {
          name: "plant[0]",
          value: listLine[0].plant
        }
      });
    }
  }, []);

  const onDeleteAll = () => {
    setOpenConfirmDelete(true);
    setDataDeleteSingle({
      id: "",
      title: "ลบทุกรายการสินค้า!",
      content: "คุณกำลังจะลบทุกรายการสินค้าในหน้านี้ทั้งหมด"
    });
  };

  useEffect(() => {
    if (validateForm?.isCheckValidate && !formik.isValid) {
      // Verify validate form
      formik.handleSubmit();
    }

    // Validate submit
    setValidateForm({
      ...validateForm,
      isValidate: {
        ...validateForm.isValidate,
        validateProduct: formik.isValid
      }
    });
  }, [validateForm?.isCheckValidate, formik.isValid]);

  const onDeleteSingle = id => {
    setOpenConfirmDelete(true);
    setDataDeleteSingle({
      id,
      title: "ลบรายการนี้",
      content: "คุณกำลังจะลบรายการสินค้านี้"
    });
  };

  const confirmDelete = () => {
    setLoading(true);
    setOpenConfirmDelete(false);
    try {
      const newData = listLine.filter(line => line.id !== dataDeleteSingle.id);
      if (!dataDeleteSingle.id) {
        setListLine([]);
      } else {
        setListLine(newData);
      }
      formik.setValues({
        quantity: newData.map(line => line.quantity),
        plant: newData.map(line => line.plant),
        requestDate: newData.map(line => line.requestDate)
      });
      setLoading(false);
    } catch {
      setLoading(false);
    }
  };

  const handleCloseModalNote = () => {
    setOpenModalNote(false);
    setDataModalNote({
      name: "",
      subTitle: "",
      title: "",
      lineId: "",
      initialNote: ""
    });
  };

  const handleSubmitModalNote = (name, note) => {
    const lines = [...listLine];
    const lineChange = lines.find(item => item.id === dataModalNote.lineId);
    lineChange[name] = note;
    setListLine(lines);
    handleCloseModalNote();
  };

  const handleOpenNoteWareHouse = line => {
    setDataModalNote({
      name: "internalCommentsToWarehouse",
      title: "บันทึกภายในถึงคลังสินค้า",
      subTitle: "สำหรับรายการสินค้านี้",
      initialNote: line.internalCommentsToWarehouse,
      lineId: line.id
    });
    setModalNoteKey(modalNoteKey + 1);
    setOpenModalNote(true);
  };

  const handleOpenNoteProductInformation = line => {
    setDataModalNote({
      name: "productInformation",
      title: "ข้อมูลเกี่ยวกับการสั่งผลิต",
      subTitle: "สำหรับรายการสินค้านี้",
      initialNote: line.productInformation,
      lineId: line.id
    });
    setModalNoteKey(modalNoteKey + 1);
    setOpenModalNote(true);
  };

  const handleApplyShipping = shipping => {
    const lines = [...listLine];
    const lineChange = lines.find(item => item.id === dataModalShipping.lineId);
    lineChange.shipTo = shipping;
    setListLine(lines);
    handleCloseShipping();
  };

  const handleCloseShipping = () => {
    setDataModalShipping({
      address: [],
      lineId: "",
      visible: false,
      initialValue: ""
    });
  };

  const openModalShipping = line => {
    setDataModalShipping({
      address:
        dataOrder?.contractOrder?.customer?.representatives[0]?.addresses,
      lineId: line.id,
      visible: true,
      initialValue: line.shipTo
    });
  };

  const handleChangePlant = (event, lineId) => {
    const lines = [...listLine];
    const lineChange = lines.find(item => item.id === lineId);
    const text = event.target.value;
    const maxLength = 4;

    if (
      !text ||
      (new RegExp("^[A-Za-z0-9]*$").test(text) &&
        (text.length <= maxLength || !maxLength))
    ) {
      formik.handleChange(event);
      lineChange.plant = text;
      setListLine(lines);
    }
  };

  const handleChangeQuantity = (event, lineId) => {
    const lines = [...listLine];
    const lineChange = lines.find(item => item.id === lineId);
    const linesSameProduct = lines.filter(
      line => line.contractProduct.id === lineChange.contractProduct.id
    );
    const remain = lineChange.contractProduct?.remain || 0;
    const quantity = event.target.value;
    lineChange.quantity = quantity;
    const amountTotal = getTotalQuantityInputByProduct(lineId);

    if (parseFloat(quantity)) {
      lineChange.errorQuantity = amountTotal > remain;
      if (amountTotal <= remain) {
        linesSameProduct.forEach(line => {
          line.errorQuantity = !parseFloat(line.quantity);
        });
      }
    } else {
      lineChange.errorQuantity = true;
    }
    formik.handleChange(event);
    setListLine(lines);
  };

  const getTotalQuantityInputByProduct = (lineId: string) => {
    let amountTotal = 0;
    const lineChange = listLine.find(line => line.id === lineId);

    listLine
      .filter(line => line.product.id === lineChange.product.id)
      .forEach(item => {
        if (!item.quantity) {
          amountTotal += 0;
        } else {
          amountTotal += parseFloat(item.quantity);
        }
      });
    return amountTotal;
  };

  const handleChangeDate = (event, lineId) => {
    const lines = [...listLine];
    const lineChange = lines.find(item => item.id === lineId);
    lineChange.requestDate = event.target.value;
    formik.handleChange(event);
    setListLine(lines);
  };

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h6" className={classes.title}>
          {intl.formatMessage({
            defaultMessage: "รายละเอียดสินค้า"
          })}
        </Typography>
      </div>
      <div className={classes.subHeader}>
        <MyTypography variant="subtitle2" className={classes.subTitle}>
          <span className={classes.normalText}>มีทั้งหมด</span>{" "}
          {listLine.length} <span className={classes.normalText}>รายการ</span>
        </MyTypography>
        <Button
          variant={"outlined"}
          color={"primary"}
          onClick={onDeleteAll}
          disabled={loading}
          className={classes.btnDelete}
        >
          <MyTypography variant="body3">ลบทุกรายการสินค้า</MyTypography>
        </Button>
      </div>
      {listLine?.map((line, index) => (
        <Paper className={classes.itemContainer} key={line.id}>
          <MyTypography variant="subtitle2" className={classes.itemTitle}>
            {index + 1}.
          </MyTypography>
          <div className={classes.itemWrapper}>
            <Grid container>
              <Grid item lg={10}>
                <Grid item lg={12}>
                  <MyTypography
                    variant="subtitle2"
                    className={classes.itemTitle}
                  >
                    {line?.product?.name} / {line?.variant?.name}
                  </MyTypography>
                </Grid>
                <div className={classes.gridContainer}>
                  <div className={classes.item1}>
                    <MyTypography
                      variant="caption"
                      className={classes.itemLabel}
                    >
                      จำนวนสั่งซื้อ
                    </MyTypography>
                    <NumberFormat
                      error={
                        !!(
                          formik.errors.quantity &&
                          formik.errors.quantity[index] &&
                          formik.touched.quantity &&
                          formik.touched.quantity[index]
                        ) || line.errorQuantity
                      }
                      name={`quantity[${index}]`}
                      value={formik.values.quantity[index]}
                      onChange={event => handleChangeQuantity(event, line.id)}
                      allowLeadingZeros
                      decimalScale={0}
                    />
                  </div>
                  <div className={classes.item2}>
                    <MyTypography
                      variant="caption"
                      className={classes.itemLabel}
                    >
                      หน่วยซื้อ
                    </MyTypography>
                    <MyTypography variant="body3">
                      {line.product.salesUnit}
                    </MyTypography>
                  </div>
                  <div className={classes.item3}>
                    <MyTypography
                      variant="caption"
                      className={classes.itemLabel}
                    >
                      น้ำหนัก <span className={classes.normalText}>(Kg.)</span>{" "}
                      / หน่วยซื้อ
                    </MyTypography>
                    <MyTypography variant="body3">
                      {line?.variant?.weight
                        ? formatThousandInteger(Number(line?.variant?.weight))
                        : ""}
                    </MyTypography>
                  </div>
                  <div className={classes.item4}>
                    <MyTypography
                      variant="caption"
                      className={classes.itemLabel}
                    >
                      ราคา <span className={classes.normalText}>(บาท)</span> /
                      หน่วยซื้อ
                    </MyTypography>
                    <MyTypography variant="body3">
                      <DecimalFormat
                        value={line?.contractProduct?.price}
                        isGrayDecimal={false}
                        countDecimal={2}
                      />
                    </MyTypography>
                  </div>
                  <div className={classes.item5}>
                    <MyTypography
                      variant="caption"
                      className={classes.itemLabel}
                    >
                      ราคารวมสุทธิ{" "}
                      <span className={classes.normalText}>(บาท)</span>
                    </MyTypography>
                    <MyTypography variant="body3">
                      <DecimalFormat
                        value={line.quantity * line.contractProduct.price}
                        isGrayDecimal={false}
                        countDecimal={2}
                      />
                    </MyTypography>
                  </div>
                  <div className={classes.item6}>
                    <MyTypography
                      variant="caption"
                      className={classes.itemLabel}
                    >
                      เงื่อนไขการชำระเงิน
                    </MyTypography>
                    <MyTypography variant="body3">
                      {line.paymentTerm}
                    </MyTypography>
                  </div>
                  <div className={classes.item7}>
                    <MyTypography
                      variant="caption"
                      className={classes.itemLabel}
                    >
                      {intl.formatMessage({
                        defaultMessage: "วันที่ต้องการสินค้า"
                      })}
                    </MyTypography>
                    <DatePicker
                      error={
                        !!(
                          formik.errors.requestDate &&
                          formik.errors.requestDate[index] &&
                          formik.touched.requestDate &&
                          formik.touched.requestDate[index]
                        )
                      }
                      name={`requestDate[${index}]`}
                      value={formik.values.requestDate[index]}
                      onChange={event => handleChangeDate(event, line.id)}
                    />
                  </div>
                  <div className={classes.item8}>
                    <MyTypography
                      variant="caption"
                      className={classes.itemLabel}
                    >
                      PLANT
                    </MyTypography>
                    <SingleTextField
                      error={
                        !!(
                          formik.errors.plant &&
                          formik.errors.plant[index] &&
                          formik.touched.plant &&
                          formik.touched.plant[index]
                        )
                      }
                      name={`plant[${index}]`}
                      onChange={event => handleChangePlant(event, line.id)}
                      value={formik.values.plant[index]}
                      placeholder={"ระบุ Plant"}
                      maxLengthProps={4}
                      regex={REGEX_ONLY_NUMBER_CHARACTER}
                    />
                  </div>
                  <div className={classes.item9}>
                    <MyTypography
                      variant="caption"
                      className={classes.itemLabel}
                    >
                      ( P )
                    </MyTypography>
                    <Button variant="contained" color="secondary">
                      <img src={externalLinkIcon} alt="" />
                    </Button>
                  </div>
                  <div className={classes.item10}>
                    <MyTypography
                      variant="caption"
                      className={classes.itemLabel}
                    >
                      Delivery Tolerance
                    </MyTypography>
                    <div className={classes.flex}>
                      <div className={classes.under}>
                        Under ({line.contractProduct?.deliveryUnder}%)
                      </div>
                      <div className={classes.over}>
                        Over ({line.contractProduct?.deliveryOver}%)
                      </div>
                    </div>
                  </div>
                </div>
              </Grid>
              <Grid item lg={2} className={classes.btnContainer}>
                <button onClick={() => onDeleteSingle(line.id)}>
                  <img src={trashTeal} alt="trash-icon" />
                </button>
                <div className={classes.btnWrapper}>
                  <MyTypography
                    variant="body3"
                    className={classes.actions}
                    onClick={() => openModalShipping(line)}
                  >
                    สถานที่จัดส่งสินค้า
                  </MyTypography>
                  <MyTypography
                    variant="body3"
                    className={classes.actions}
                    onClick={() => handleOpenNoteWareHouse(line)}
                  >
                    บันทึกภายในถึงคลังสินค้า
                  </MyTypography>
                  <MyTypography
                    variant="body3"
                    className={classes.actions}
                    onClick={() => handleOpenNoteProductInformation(line)}
                  >
                    ข้อมูลเกี่ยวกับการสั่งผลิต
                  </MyTypography>
                </div>
              </Grid>
            </Grid>
          </div>
        </Paper>
      ))}

      <DialogConfirmDelete
        onCancel={() => setOpenConfirmDelete(false)}
        onConfirm={confirmDelete}
        open={openConfrmDelete}
        title={dataDeleteSingle.title}
        content={dataDeleteSingle.content}
      />
      <ModalNote
        key={modalNoteKey}
        open={openModalNote}
        onClose={handleCloseModalNote}
        onSubmit={handleSubmitModalNote}
        name={dataModalNote.name}
        initialNote={dataModalNote.initialNote}
        title={dataModalNote.title}
        subTitle={dataModalNote.subTitle}
      />
      <ModalShipping
        type={ShippingTypeEnum.Delivery}
        address={dataModalShipping.address}
        visible={dataModalShipping.visible}
        initialValue={dataModalShipping.initialValue}
        handleApplyShipping={handleApplyShipping}
        onClose={handleCloseShipping}
        mainTitle={"เลือกสถานที่จัดส่งสินค้า"}
        subTitle={"(สำหรับรายการสินค้านี้)"}
      />
    </div>
  );
};

export default ProductDetails;
